import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const ReminderSchedulePanel = ({ onSuccess }) => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [showStepForm, setShowStepForm] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [saving, setSaving] = useState(false);

  const [scheduleForm, setScheduleForm] = useState({ name: '', description: '', is_default: false });
  const [stepForm, setStepForm] = useState({
    step_number: 1,
    days_before_deadline: 1,
    action_type: 'email',
    subject: 'Reminder: Your approval is needed',
    message_template: 'Dear {{customer_name}}, this is a reminder that your approval for {{reference}} is due on {{deadline}}.',
    is_active: true,
  });

  useEffect(() => { fetchSchedules(); }, []);

  const fetchSchedules = async () => {
    try {
      setLoading(true);
      const { data } = await supabase
        ?.from('approval_reminder_schedules')
        ?.select('*, approval_reminder_steps(*)')
        ?.order('created_at');
      setSchedules(data ?? []);
    } catch (err) {
      console.error('Error fetching schedules:', err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSchedule = async () => {
    try {
      setSaving(true);
      if (selectedSchedule?.id) {
        await supabase?.from('approval_reminder_schedules')?.update({
          name: scheduleForm?.name,
          description: scheduleForm?.description,
          is_default: scheduleForm?.is_default,
          updated_at: new Date()?.toISOString(),
        })?.eq('id', selectedSchedule?.id);
      } else {
        await supabase?.from('approval_reminder_schedules')?.insert([scheduleForm]);
      }
      setShowScheduleForm(false);
      setSelectedSchedule(null);
      setScheduleForm({ name: '', description: '', is_default: false });
      onSuccess?.('Schedule saved successfully');
      fetchSchedules();
    } catch (err) {
      console.error('Error saving schedule:', err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveStep = async () => {
    try {
      setSaving(true);
      await supabase?.from('approval_reminder_steps')?.insert([{
        ...stepForm,
        schedule_id: selectedSchedule?.id,
      }]);
      setShowStepForm(false);
      setStepForm({ step_number: 1, days_before_deadline: 1, action_type: 'email', subject: 'Reminder: Your approval is needed', message_template: 'Dear {{customer_name}}, this is a reminder that your approval for {{reference}} is due on {{deadline}}.', is_active: true });
      onSuccess?.('Reminder step added');
      fetchSchedules();
    } catch (err) {
      console.error('Error saving step:', err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStep = async (stepId) => {
    if (!window.confirm('Delete this reminder step?')) return;
    await supabase?.from('approval_reminder_steps')?.delete()?.eq('id', stepId);
    onSuccess?.('Step deleted');
    fetchSchedules();
  };

  const handleToggleSchedule = async (schedule) => {
    await supabase?.from('approval_reminder_schedules')?.update({
      is_active: !schedule?.is_active,
      updated_at: new Date()?.toISOString(),
    })?.eq('id', schedule?.id);
    onSuccess?.(`Schedule ${!schedule?.is_active ? 'activated' : 'deactivated'}`);
    fetchSchedules();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-foreground">Reminder Schedules</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Configure automated reminder emails for pending approvals</p>
        </div>
        <Button
          size="sm"
          onClick={() => { setSelectedSchedule(null); setScheduleForm({ name: '', description: '', is_default: false }); setShowScheduleForm(true); }}
        >
          <Icon name="Plus" size={14} className="mr-1" /> New Schedule
        </Button>
      </div>
      {schedules?.length === 0 && (
        <div className="text-center py-10 text-muted-foreground">
          <Icon name="BellOff" size={36} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">No reminder schedules configured</p>
        </div>
      )}
      {schedules?.map((schedule) => (
        <div key={schedule?.id} className="border border-border rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-muted/30">
            <div className="flex items-center gap-3">
              <div className={`w-2 h-2 rounded-full ${schedule?.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
              <div>
                <span className="font-semibold text-sm text-foreground">{schedule?.name}</span>
                {schedule?.is_default && (
                  <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">Default</span>
                )}
                {schedule?.description && (
                  <p className="text-xs text-muted-foreground">{schedule?.description}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleToggleSchedule(schedule)}
                className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${
                  schedule?.is_active
                    ? 'bg-green-100 text-green-700 hover:bg-green-200' :'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {schedule?.is_active ? 'Active' : 'Inactive'}
              </button>
              <button
                onClick={() => { setSelectedSchedule(schedule); setShowStepForm(true); }}
                className="text-xs px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 font-medium transition-colors"
              >
                + Add Step
              </button>
            </div>
          </div>

          {(schedule?.approval_reminder_steps ?? [])?.length === 0 ? (
            <div className="px-4 py-3 text-xs text-muted-foreground italic">No steps configured yet</div>
          ) : (
            <div className="divide-y divide-border">
              {(schedule?.approval_reminder_steps ?? [])?.sort((a, b) => a?.step_number - b?.step_number)?.map((step) => (
                <div key={step?.id} className="flex items-center justify-between px-4 py-2.5">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
                      {step?.step_number}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {step?.days_before_deadline === 0
                          ? 'On deadline day'
                          : `${step?.days_before_deadline} day${step?.days_before_deadline !== 1 ? 's' : ''} before deadline`}
                      </p>
                      <p className="text-xs text-muted-foreground">{step?.subject}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      step?.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {step?.action_type}
                    </span>
                    <button
                      onClick={() => handleDeleteStep(step?.id)}
                      className="p-1 rounded hover:bg-red-50 text-red-500 transition-colors"
                    >
                      <Icon name="Trash2" size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      {/* Schedule Form Modal */}
      {showScheduleForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="font-semibold text-foreground mb-4">{selectedSchedule ? 'Edit Schedule' : 'New Reminder Schedule'}</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Schedule Name *</label>
                <input
                  type="text"
                  value={scheduleForm?.name}
                  onChange={(e) => setScheduleForm(f => ({ ...f, name: e?.target?.value }))}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="e.g. Standard Approval Reminders"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Description</label>
                <textarea
                  value={scheduleForm?.description}
                  onChange={(e) => setScheduleForm(f => ({ ...f, description: e?.target?.value }))}
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={scheduleForm?.is_default}
                  onChange={(e) => setScheduleForm(f => ({ ...f, is_default: e?.target?.checked }))}
                  className="rounded"
                />
                <span className="text-sm text-foreground">Set as default schedule</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" size="sm" onClick={() => setShowScheduleForm(false)}>Cancel</Button>
              <Button size="sm" loading={saving} onClick={handleSaveSchedule} disabled={!scheduleForm?.name?.trim()}>Save Schedule</Button>
            </div>
          </div>
        </div>
      )}
      {/* Step Form Modal */}
      {showStepForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="font-semibold text-foreground mb-4">Add Reminder Step</h3>
            <p className="text-xs text-muted-foreground mb-4">For schedule: <strong>{selectedSchedule?.name}</strong></p>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Step Number</label>
                  <input
                    type="number"
                    min={1}
                    value={stepForm?.step_number}
                    onChange={(e) => setStepForm(f => ({ ...f, step_number: parseInt(e?.target?.value) || 1 }))}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Days Before Deadline</label>
                  <input
                    type="number"
                    min={0}
                    value={stepForm?.days_before_deadline}
                    onChange={(e) => setStepForm(f => ({ ...f, days_before_deadline: parseInt(e?.target?.value) || 0 }))}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Email Subject</label>
                <input
                  type="text"
                  value={stepForm?.subject}
                  onChange={(e) => setStepForm(f => ({ ...f, subject: e?.target?.value }))}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Message Template</label>
                <textarea
                  value={stepForm?.message_template}
                  onChange={(e) => setStepForm(f => ({ ...f, message_template: e?.target?.value }))}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                />
                <p className="text-xs text-muted-foreground mt-1">Variables: {'{{customer_name}}'}, {'{{reference}}'}, {'{{deadline}}'}</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" size="sm" onClick={() => setShowStepForm(false)}>Cancel</Button>
              <Button size="sm" loading={saving} onClick={handleSaveStep}>Add Step</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReminderSchedulePanel;
